import React, { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Collapse from '@mui/material/Collapse';
import ResponsiveTabs from 'components/ResponsiveTabs/ResponsiveTabs';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import { jsPDF } from 'jspdf';
import jsonToTable from 'json-to-table';
import GrillaEffect from 'views/Grillas/GrillaEffect';
import dayjs from 'dayjs';
import DataLabel from 'views/Cards/DataLabel';

// Formatea un importe (número o string) a "$ 1.234,56".
const fmtImporte = (v) => {
  const n = Number(String(v).replace(/[^0-9\-,.]/g, '').replace(',', '.'));
  return isNaN(n) ? v : '$ ' + n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const parseImporte = (v) => {
  const n = Number(String(v).replace(/[^0-9\-,.]/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

// Tabla genérica de un nivel de detalle del día (label + importe + total).
// La usan las dos secciones del panel: entes (query 63) y vencimientos (query 94).
function TablaDetalleDia({ periodo, dia, queryId, sorting, labelKey, colLabel }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          id: queryId, page: 0, size: 1000,
          filters: JSON.stringify([
            { id: 'periodo_cobranza', value: periodo },
            { id: 'dia_cobranza', value: String(dia) },
          ]),
          sorting: JSON.stringify(sorting),
        });
        const res = await fetch(`${process.env.REACT_APP_API_URL}/querysextgrilla/?${params}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') },
        });
        const json = await res.json();
        if (!cancel) setRows(json.data ?? []);
      } catch (e) {
        if (!cancel) setRows([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [periodo, dia, queryId]);

  const total = rows.reduce((s, r) => s + parseImporte(r.importe), 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pl: 1 }}>
        <CircularProgress size={14} sx={{ color: '#8ebf63' }} />
        <Typography variant="caption" sx={{ color: '#757575' }}>Cargando…</Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 280 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5', borderBottom: '2px solid #8ebf63' }}>{colLabel}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#f5f5f5', borderBottom: '2px solid #8ebf63' }}>Importe</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} sx={{ '&:hover': { bgcolor: '#e8f4d9' } }}>
              <TableCell sx={{ fontSize: '0.78rem' }}>{r[labelKey]}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.78rem' }}>{fmtImporte(r.importe)}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={2} sx={{ color: '#757575', fontSize: '0.78rem' }}>Sin datos para este día</TableCell></TableRow>
          )}
          {rows.length > 0 && (
            <TableRow sx={{ bgcolor: '#eef6e3' }}>
              <TableCell sx={{ fontWeight: 800, fontSize: '0.78rem' }}>Total del día</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800, fontSize: '0.78rem', color: '#39a82d' }}>{fmtImporte(total)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// Sección colapsable (un "desplegable"). El hijo se monta recién al abrir (lazy fetch).
function SeccionColapsable({ titulo, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box sx={{ mb: 1 }}>
      <Box onClick={() => setOpen(o => !o)} sx={{
        display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
        py: 0.5, px: 1, bgcolor: '#eef6e3', borderRadius: 1, userSelect: 'none',
        '&:hover': { bgcolor: '#e2f0d4' },
      }}>
        <Box component="span" sx={{ fontSize: '0.8rem', color: '#39a82d', width: 14, textAlign: 'center' }}>
          {open ? '▾' : '▸'}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#161616' }}>{titulo}</Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ pt: 0.5 }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

// Detalle expandible del día (Mantis #304/#305): dos secciones colapsables —
// entes de cobranza y fechas de vencimiento. Cada una cierra con el total del día.
function DetalleDia({ periodo, dia }) {
  return (
    <Box sx={{ px: 2, py: 1, bgcolor: '#f8fdf4' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#161616', display: 'block', mb: 0.75 }}>
        Detalle — {periodo} · día {dia}
      </Typography>
      <SeccionColapsable titulo="Por ente de cobranza" defaultOpen>
        <TablaDetalleDia periodo={periodo} dia={dia} queryId={63}
          labelKey="ente" colLabel="Ente" sorting={[{ id: 'importe', desc: true }]} />
      </SeccionColapsable>
      <SeccionColapsable titulo="Por fecha de vencimiento (últimos 6 meses + Anteriores)">
        <TablaDetalleDia periodo={periodo} dia={dia} queryId={94}
          labelKey="venc" colLabel="Vencimiento" sorting={[{ id: 'ord', desc: true }]} />
      </SeccionColapsable>
    </Box>
  );
}

function Cobranzas() {
  const url = `${process.env.REACT_APP_API_URL}/querysextgrilla/`;
  const defaultFecha = [
    { id: 'periodo_cobranza', value: dayjs().subtract(1, 'month').format('YYYY-MM') }
  ];
  const [dataPdoEnte, setDataPdoEnte] = useState([]);
  const [dataPdoEnteDia, setDataPdoEnteDia] = useState([]);
  const [dataPdoDia, setDataPdoDia] = useState([]);
  const [filterCobranza, setfilterCobranza] = useState([...defaultFecha]);
  const [tab, setTab] = useState(0);

  // Selector desplegable de período (Mantis #297): últimos 15 meses, más reciente primero.
  // La vista de Cobranza retiene ~12 meses + el mes en curso; sobra con 15 opciones.
  const periodOptions = useMemo(
    () => Array.from({ length: 15 }, (_, i) => dayjs().subtract(i, 'month').format('YYYY-MM')),
    []
  );
  const colPeriodo = { caption: 'Pdo. Cobranza', enableColumnFilter: true, periodOptions };

  // Mantis #299: los conceptos no financieros (Bono/Planes/Municipalidad) llegan con
  // es_no_financiero=1 (query 62) y se muestran destacados (negrita + color).
  const colEnteDestacado = {
    caption: 'Ente',
    enableColumnFilter: true,
    Cell: ({ row, cell }) => {
      const noFin = String(row.original?.es_no_financiero) === '1';
      return (
        <span style={{
          fontWeight: noFin ? 700 : 400,
          color: noFin ? '#b26a00' : 'inherit',
          fontStyle: noFin ? 'italic' : 'normal',
        }}>
          {cell.getValue()}
        </span>
      );
    },
  };

  const colsPdoEnte = {
    periodo_cobranza: colPeriodo,
    ente: colEnteDestacado,
    importe: { caption: 'Importe', enableColumnFilter: false },
  };

  const colsPdoEnteDia = {
    periodo_cobranza: colPeriodo,
    dia_cobranza: { caption: 'Dia Cobranza', enableColumnFilter: true },
    ente: { caption: 'Ente', enableColumnFilter: true },
    importe: { caption: 'Importe', enableColumnFilter: false },
  };

  const colsPdoDia = {
    periodo_cobranza: colPeriodo,
    dia_cobranza: { caption: 'Dia Cobranza', enableColumnFilter: true },
    importe: { caption: 'Importe', enableColumnFilter: false },
  };

  const csvConfig = mkConfig({
    fieldSeparator: ',',
    decimalSeparator: '.',
    useKeysAsHeaders: true,
  });
  const GenerarCsv = async () => {
    try {
      const baseURL = `${process.env.REACT_APP_API_URL}/querysext/19`;
      const rest = await fetch(baseURL, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token'),
        },
        body: JSON.stringify({
          fdesde: new Date(startDate).toLocaleDateString(),
          fhasta: new Date(endDate).toLocaleDateString()
        })
      });
      const resulte = await rest.json();
      const csv = generateCsv(csvConfig)(resulte);
      download(csvConfig)(csv);
    } catch (error) {
      console.error(`Error al  generar csv : ${error.message}`);
      throw error;
    }
  };

  const GenerarPdf = async () => {
    try {
      const baseURL = `${process.env.REACT_APP_API_URL}/querysext/19`;
      const rest = await fetch(baseURL, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('token'),
        },
        body: JSON.stringify({
          fdesde: new Date(startDate).toLocaleDateString(),
          fhasta: new Date(endDate).toLocaleDateString()
        })
      });
      const resulte = await rest.json();
      const tabled = jsonToTable(resulte);
      let nombresCampos = tabled[0];
      let datos = tabled.shift();
      const doc = new jsPDF();
      doc.autoTable({
        head: [nombresCampos],
        body: tabled,
        margin: { top: 10 },
        styles: {
          minCellHeight: 4,
          halign: 'left',
          valign: 'center',
          fontSize: 4,
        },
      });
      doc.save('pdf-cobranzas.pdf');
    } catch (error) {
      console.error(`Error al  generar csv : ${error.message}`);
      throw error;
    }
  };

  return (
    <Box sx={{ pt: '120px' }}>
      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card minH='125px'>
            <CardBody px='2px'>
              <DataLabel
                url={url + '?id=' + 64}
                columns={colsPdoEnteDia}
                filters={filterCobranza}
                label={"TOTAL COBRADO"}
              />
            </CardBody>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card minH='125px'>
            <CardBody px='2px'>
              {/* Mantis #300: total cobrado neto (query 93), excluye Bono/Planes/Municipalidad */}
              <DataLabel
                url={url + '?id=' + 93}
                columns={colsPdoEnteDia}
                filters={filterCobranza}
                label={"TOTAL COBRADO NETO"}
              />
            </CardBody>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card minH='125px'>
            <CardBody px='2px'>
              <DataLabel
                filters={filterCobranza}
                isMoney={false}
                countDateColumn={'periodo_cobranza'}
                countDayColumn={'dia_cobranza'}
                label={"CANTIDAD DE DIAS"}
              />
            </CardBody>
          </Card>
        </Grid>
        {/* BOTONES EXPORTAR — pendiente activar
        <Grid item xs={12} sm={6} md={4}>
          <Card minH='125px'>
            <Box sx={{ display:'flex', gap:2, p:2 }}>
              <Button variant="outlined" onClick={GenerarCsv}>
                Exportar CSV
              </Button>
              <Button variant="outlined" color="error" onClick={GenerarPdf}>
                Exportar PDF
              </Button>
            </Box>
          </Card>
        </Grid>
        */}
      </Grid>

      {/* Tabs + Grillas */}
      <Card sx={{ p: 2 }}>
            <CardBody>
              <ResponsiveTabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                tabs={[
                  { label: 'Por Periodo y Ente' },
                  { label: 'Por Periodo y Dia' },
                ]}
              />
              {tab === 0 && (
                <Box pt={1}>
                  <Typography variant="caption" sx={{ color: '#757575', display: 'block', mb: 0.75 }}>
                    <b style={{ color: '#b26a00' }}>Bono Energético, Planes de Pago y Municipalidad</b> se muestran
                    destacados y ordenados al final: no representan ingreso financiero directo. El indicador{' '}
                    <b>Total Cobrado Neto</b> los excluye.
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                    <GrillaEffect
                      data={dataPdoEnte} updateData={setDataPdoEnte}
                      filters={filterCobranza} updateFilters={setfilterCobranza}
                      idRemoto={62}
                      colsHeaders={colsPdoEnte}
                      defaultSorting={[
                        { id: 'periodo_cobranza', desc: true },
                        { id: 'es_no_financiero', desc: false },
                        { id: 'ente', desc: false }
                      ]} />
                  </LocalizationProvider>
                </Box>
              )}
              {tab === 1 && (
                <Box pt={1}>
                  <Typography variant="caption" sx={{ color: '#757575', display: 'block', mb: 0.75 }}>
                    Expandí un día (▸) para ver el detalle: <b>por ente de cobranza</b> y <b>por fecha de vencimiento</b>.
                    Cada apertura suma el total del día.
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                    <GrillaEffect
                      data={dataPdoDia} updateData={setDataPdoDia}
                      filters={filterCobranza} updateFilters={setfilterCobranza}
                      idRemoto={71}
                      colsHeaders={colsPdoDia}
                      defaultSorting={[
                        { id: 'periodo_cobranza', desc: true },
                        { id: 'dia_cobranza', desc: false },
                      ]}
                      renderDetailPanel={({ row }) => (
                        <DetalleDia
                          periodo={row.original.periodo_cobranza}
                          dia={row.original.dia_cobranza}
                        />
                      )} />
                  </LocalizationProvider>
                </Box>
              )}
            </CardBody>
          </Card>
    </Box>
  );
}

export default Cobranzas;
